/** Single source of mutation for the session token.
 *
 * Backed by `sessionStorage` so the token is cleared on browser close —
 * appropriate for an internal high-privilege portal. The in-memory cache
 * keeps reads fast on the hot request path.
 *
 * Cross-tab: sessionStorage is per-tab, so a `BroadcastChannel` handshake
 * shares the LIVE session between same-origin tabs without persisting the
 * token anywhere new — a fresh tab asks living tabs for the token at boot
 * (`adoptTokenFromOtherTabs`), and every set/rotate/clear is broadcast.
 * Broadcasting is not optional: `POST /auth/token/rotate/` deletes the old
 * token server-side, so once tabs share a session, a rotation in one tab
 * must reach the others before their next request.
 *
 * The whole file is the swap point if we ever move to httpOnly cookies:
 * change these functions and nothing else.
 */
const KEY_TOKEN = 'cp_console_token';
const KEY_EXPIRES = 'cp_console_token_expires';
const CHANNEL_NAME = 'cp_console_auth';

type AuthMessage =
  | { type: 'request' }
  | { type: 'set'; token: string; expiresAt: string | null }
  | { type: 'clear' };

let memoryToken: string | null = null;
let memoryExpiresAt: string | null = null;

/** Fired on CROSS-TAB adoption/clear only (local set/clear callers already
 *  know what they did). */
type TokenChangeListener = (hasToken: boolean) => void;
const listeners = new Set<TokenChangeListener>();

function readFromSession() {
  try {
    memoryToken = window.sessionStorage.getItem(KEY_TOKEN);
    memoryExpiresAt = window.sessionStorage.getItem(KEY_EXPIRES);
  } catch {
    // SSR / no DOM — stay null.
  }
}

// Hydrate once at module load.
readFromSession();

function writeToSession(token: string | null, expiresAt: string | null) {
  try {
    if (token) {
      window.sessionStorage.setItem(KEY_TOKEN, token);
    } else {
      window.sessionStorage.removeItem(KEY_TOKEN);
    }
    if (expiresAt) {
      window.sessionStorage.setItem(KEY_EXPIRES, expiresAt);
    } else {
      window.sessionStorage.removeItem(KEY_EXPIRES);
    }
  } catch {
    // Tab / private mode without storage — keep going on the in-memory copy.
  }
}

// ---------------------------------------------------------------------------
// BroadcastChannel glue (silent no-op where unavailable — behavior then
// matches the pre-handshake world: each tab authenticates on its own).
// ---------------------------------------------------------------------------

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (channel) return channel;
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent) => handleMessage(event.data as AuthMessage);
    // Node's BroadcastChannel (vitest) holds the event loop open; browsers
    // don't have unref — optional call keeps both happy.
    (channel as unknown as { unref?: () => void }).unref?.();
  } catch {
    channel = null;
  }
  return channel;
}

function post(message: AuthMessage) {
  try {
    getChannel()?.postMessage(message);
  } catch {
    // Channel closed / serialization issue — cross-tab sync degrades, local
    // auth keeps working.
  }
}

/** Adopt a token that arrived from another tab (never re-broadcast). */
function applyRemote(token: string | null, expiresAt: string | null) {
  const hadToken = memoryToken !== null;
  memoryToken = token;
  memoryExpiresAt = expiresAt;
  writeToSession(token, expiresAt);
  const hasToken = token !== null;
  if (hadToken !== hasToken || hasToken) {
    for (const cb of listeners) cb(hasToken);
  }
}

function handleMessage(message: AuthMessage | null | undefined) {
  if (!message || typeof message !== 'object') return;
  if (message.type === 'request') {
    if (memoryToken) post({ type: 'set', token: memoryToken, expiresAt: memoryExpiresAt });
    return;
  }
  if (message.type === 'set' && typeof message.token === 'string' && message.token) {
    if (message.token !== memoryToken) {
      applyRemote(message.token, message.expiresAt ?? null);
    }
    return;
  }
  if (message.type === 'clear') {
    if (memoryToken !== null) applyRemote(null, null);
  }
}

// Start listening immediately so this tab can answer peers' requests.
getChannel();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getToken(): string | null {
  return memoryToken;
}

export function getTokenExpiresAt(): string | null {
  return memoryExpiresAt;
}

export function setToken(token: string, expiresAt: string | null = null): void {
  memoryToken = token;
  memoryExpiresAt = expiresAt;
  writeToSession(token, expiresAt);
  post({ type: 'set', token, expiresAt });
}

export function clearToken(): void {
  memoryToken = null;
  memoryExpiresAt = null;
  writeToSession(null, null);
  post({ type: 'clear' });
}

/** Ask living tabs for the current session token. Resolves `true` as soon as
 *  a token is available (already present, or adopted from a peer), `false`
 *  after `timeoutMs` with no reply — the caller then proceeds logged-out.
 *  Costs latency only on genuinely token-less boots. */
export function adoptTokenFromOtherTabs(timeoutMs = 300): Promise<boolean> {
  if (memoryToken) return Promise.resolve(true);
  if (!getChannel()) return Promise.resolve(false);
  return new Promise((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      listeners.delete(onChange);
      clearTimeout(timer);
      resolve(ok);
    };
    const onChange: TokenChangeListener = (hasToken) => {
      if (hasToken) finish(true);
    };
    listeners.add(onChange);
    const timer = setTimeout(() => finish(false), timeoutMs);
    post({ type: 'request' });
  });
}

/** Subscribe to CROSS-TAB token changes (adoption from a peer, remote rotate,
 *  remote logout). Returns the unsubscribe function. */
export function subscribeTokenChange(cb: TokenChangeListener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
